import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { X, Warning } from '@phosphor-icons/react';

import { ApiError } from '@/api/client';
import { transferOwnership, type Administrator } from '@/api/admins';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// TransferOwnerModal hands the org keys to a different administrator. Mirrors
// the soft-delete flow: the user types the org name as a typed-confirmation
// gate, server re-checks the match. The previous owner is auto-demoted to
// administrator inside the same transaction so the org never sits without an
// owner.
//
// Visible only to the current owner (Administrators page filters the button
// behind callerRole === 'owner'). The target must already be an active
// administrator — leaders and pending invitations don't qualify, and the
// component renders nothing if asked to transfer to either kind of row.
export interface TransferOwnerModalProps {
  open: boolean;
  onClose: () => void;
  slug: string;
  orgName: string;
  target: Administrator | null;
}

export function TransferOwnerModal({
  open,
  onClose,
  slug,
  orgName,
  target,
}: TransferOwnerModalProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [typed, setTyped] = useState('');
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      setTyped('');
      setErrorCode(null);
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!target?.user_id) {
        return Promise.reject(new Error('missing_target'));
      }
      return transferOwnership(token, slug, target.user_id, typed.trim());
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['administrators', slug] });
      // Caller's own role changed — refresh the org list so the sidebar /
      // settings panel re-evaluates ownerOnly tiles immediately.
      void queryClient.invalidateQueries({ queryKey: ['my-orgs'] });
      onClose();
    },
    onError: (err) => {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
    },
  });

  if (!target) return null;

  const matches = typed.trim() === orgName;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!matches || mutation.isPending) return;
    setErrorCode(null);
    mutation.mutate();
  };

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex w-[min(34rem,92vw)] flex-col gap-4 p-6"
      >
        <header className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-destructive/10 text-destructive"
            >
              <Warning className="h-5 w-5" />
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground">
              {t('settings.administrators.transferOwner.title')}
            </h2>
          </div>
          <button
            type="button"
            aria-label={t('common.cancel')}
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <p className="text-sm text-foreground">
          {t('settings.administrators.transferOwner.body', {
            display_name: target.display_name,
            email: target.email,
            org_name: orgName,
          })}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('settings.administrators.transferOwner.demotionNote')}
        </p>

        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('settings.administrators.transferOwner.confirmLabel', { org_name: orgName })}
          <Input
            type="text"
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
              setErrorCode(null);
            }}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            placeholder={orgName}
          />
        </label>

        {errorCode && (
          <p role="alert" className="text-sm text-destructive">
            {t(`settings.administrators.transferOwner.error.${errorCode}`, {
              defaultValue: t('settings.administrators.transferOwner.error.unknown'),
            })}
          </p>
        )}

        <footer className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="destructive"
            disabled={!matches || mutation.isPending}
          >
            {mutation.isPending
              ? t('settings.administrators.transferOwner.submitting')
              : t('settings.administrators.transferOwner.cta')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
